import { useCallback, useEffect, useMemo } from "react"

import { AskStatus } from "@f2e/shared/types"
import { ConversationErrorType } from "@f2e/ui/chat"
import { useChatSessionV2 } from "@f2e/ui/chat-v2"

import type { ConversationContext, History } from "@/types"
import useSysConfig from "./useSysConfig"
import { redirectToLoginForExpiredSession } from "@/lib/authRedirect"

const STORAGE_KEY_LAST_HISTORY_ID = "copilot.lastHistoryId"

export const CONVERSATION_LIMIT = 100
export { AskStatus }

export interface UseCopilotSessionOptions {
  workspaceId?: string
  getUserLocale?: () => string | undefined
  onHistoryPrepared?: (historyId: number) => void
  onError?: (errorType: ConversationErrorType, message?: string) => void
}

const useCopilotSession = (options: UseCopilotSessionOptions = {}) => {
  const { serverRoot } = useSysConfig()
  const storageKey = STORAGE_KEY_LAST_HISTORY_ID
  const legacyWorkspaceStorageKey = options.workspaceId
    ? `${STORAGE_KEY_LAST_HISTORY_ID}.${options.workspaceId}`
    : undefined

  const baseUrl = useMemo(() => `${serverRoot}/api/v1`, [serverRoot])

  const chatSession = useChatSessionV2({
    getBaseUrl: () => baseUrl,
    getStreamMetadata: () => ({
      mode: "copilot",
      currentPageUrl:
        typeof window !== "undefined" ? window.location.href : undefined,
      viewingWorkspaceId: options.workspaceId,
      userLocale:
        options.getUserLocale?.() ??
        (typeof navigator !== "undefined" ? navigator.language : undefined),
    }),
    conversationLimit: CONVERSATION_LIMIT,
    onError: (errorType, message) => {
      options.onError?.(errorType, message)
    },
    onUnauthorized: redirectToLoginForExpiredSession,
    onHistoryIdReceived: (historyId) => {
      options.onHistoryPrepared?.(historyId)
    },
    onComplete: (historyId) => {
      if (historyId !== undefined) {
        options.onHistoryPrepared?.(historyId)
      }
    },
  })

  const historyId = chatSession.history?.id
  const isAsking = chatSession.askingStatus === AskStatus.Answering

  const rememberHistoryId = useCallback(
    (id?: number) => {
      if (id !== undefined && id > 0) {
        localStorage.setItem(storageKey, String(id))
        if (legacyWorkspaceStorageKey) {
          localStorage.removeItem(legacyWorkspaceStorageKey)
        }
      }
    },
    [legacyWorkspaceStorageKey, storageKey]
  )

  useEffect(() => {
    rememberHistoryId(historyId)
  }, [historyId, rememberHistoryId])

  const initConversation = useCallback(
    (context: ConversationContext) => {
      localStorage.removeItem(storageKey)
      if (legacyWorkspaceStorageKey) {
        localStorage.removeItem(legacyWorkspaceStorageKey)
      }
      chatSession.initConversation({
        ...context,
      })
    },
    [chatSession, legacyWorkspaceStorageKey, storageKey]
  )

  const loadConversationParts = useCallback(
    async (targetHistoryId: number) => {
      const history: History = {
        id: targetHistoryId,
        name: "",
        createdAt: "",
        updatedAt: "",
        workspace: {
          id: "",
          name: "",
          default: false,
          createdAt: "",
          updatedAt: "",
        },
      }
      const parts = await chatSession.loadConversations(history)
      if (parts !== undefined) {
        rememberHistoryId(targetHistoryId)
      }
      return parts
    },
    [chatSession, rememberHistoryId]
  )

  const getLastHistoryId = useCallback((): number | undefined => {
    const parseStoredHistoryId = (value: string | null): number | undefined => {
      if (!value) return undefined
      const trimmed = value.trim()
      if (!/^-?\d+$/.test(trimmed)) return undefined
      const id = Number(trimmed)
      if (!Number.isInteger(id) || id < 0) return undefined
      return id
    }

    const storedId = parseStoredHistoryId(localStorage.getItem(storageKey))
    if (storedId !== undefined) {
      return storedId
    }

    const legacyStoredId = parseStoredHistoryId(
      legacyWorkspaceStorageKey
        ? localStorage.getItem(legacyWorkspaceStorageKey)
        : null
    )
    if (legacyStoredId !== undefined) {
      localStorage.setItem(storageKey, String(legacyStoredId))
      if (legacyWorkspaceStorageKey) {
        localStorage.removeItem(legacyWorkspaceStorageKey)
      }
      return legacyStoredId
    }

    return undefined
  }, [legacyWorkspaceStorageKey, storageKey])

  return {
    chatSession,
    historyId,
    askingStatus: chatSession.askingStatus,
    isAsking,
    parts: chatSession.parts,
    initConversation,
    loadConversationParts,
    getLastHistoryId,
  }
}

export default useCopilotSession
